import { HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { cloneDeep } from 'lodash';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  addToPayloadIfNotNull,
  AjaxData,
  AjaxService,
  formUrlEncodedHttpOptions,
  blankHttpParamsWithCustomEncoder,
} from '../core/ajax.service';
import { AppConfig, AppConfigService } from '../core/app-config.service';
import { CustomEncoder } from '../common/customer-encoder';
import { switchMap } from 'rxjs/operators';
import { ApiRoutesV1, ApiRoutesV2 } from '../common/routes';
import { Customer, NewCustomer, NewCustomerJson, sendingRequestStatus } from './customer-common';
import { SmsUsagesService } from '../core/sms-usages.service';
import { EmailUsagesService } from '../core/email-usages.service';
import { CustomerVoiceApiService } from '@vendasta/customer-voice-service';

@Injectable()
export class CustomerService {
  constructor(
    private readonly ajaxService: AjaxService,
    private readonly appConfig: AppConfigService,
    private readonly smsUsageService: SmsUsagesService,
    private readonly emailUsageService: EmailUsagesService,
    private readonly customerVoiceService: CustomerVoiceApiService,
  ) {}

  get customers$(): Observable<Customer[]> {
    return this.customers$$.asObservable();
  }

  get tags$(): Observable<string[]> {
    return this.tags$$.asObservable();
  }

  public customers$$: BehaviorSubject<Customer[]> = new BehaviorSubject([]);

  totalCustomers = 0;
  loadingCustomers = true;

  private customersThatHasRecentlySentEmail: {
    [contactId: string]: Customer;
  } = {};
  private customersThatHasRecentlySentSms: {
    [contactId: string]: Customer;
  } = {};

  private tags$$: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);

  public currentFilters: CustomerFilterData = {
    emailStatuses: [],
    smsStatuses: [],
    lastRequestedDateFrom: '',
    lastRequestedDateTo: '',
    createdDateFrom: '',
    createdDateTo: '',
    tag: [],
  };
  public currentPageSize = 10;

  public firstLoad(nbCustomersToLoad: number): number {
    if (nbCustomersToLoad > 0 && nbCustomersToLoad <= 10) {
      this.currentPageSize = 10;
    } else if (nbCustomersToLoad <= 50) {
      this.currentPageSize = 50;
    } else if (nbCustomersToLoad <= 100) {
      this.currentPageSize = 100;
    } else if (nbCustomersToLoad <= 150) {
      this.currentPageSize = 150;
    } else if (nbCustomersToLoad <= 500) {
      this.currentPageSize = 500;
    }
    this.loadCustomers(0, this.currentPageSize, '', this.currentFilters);
    return this.currentPageSize;
  }

  public loadCustomers(
    cursor: number,
    pageSize: number,
    searchTerm: string,
    customerFilterData: CustomerFilterData,
  ): void {
    this.currentPageSize = pageSize;
    this.currentFilters = customerFilterData;

    this.ajaxService
      .get(ApiRoutesV2.customerLookup, {
        params: {
          pageOffset: btoa(String(cursor)),
          pageLimit: String(pageSize),
          searchTerm: searchTerm,
          emailStatuses: customerFilterData.emailStatuses,
          smsStatuses: customerFilterData.smsStatuses,
          lastRequestedGte: customerFilterData.lastRequestedDateFrom,
          lastRequestedLte: customerFilterData.lastRequestedDateTo,
          createdGte: customerFilterData.createdDateFrom,
          createdLte: customerFilterData.createdDateTo,
          tags: customerFilterData.tag,
        },
      })
      .subscribe((response: HttpResponse<AjaxData>) => {
        const loadedCustomers = this.switchRequestStatusToSendingIfRecentlySentRequest(
          customersFromResponse(response.body.data['customers']),
        );
        this.customers$$.next(loadedCustomers);
        this.totalCustomers = response.body.data['total_results'];
        this.loadingCustomers = false;
      });
  }

  public getCustomersBySearchTerm(searchTerm: string): Observable<HttpResponse<AjaxData>> {
    return this.ajaxService.get(ApiRoutesV2.customerLookup, {
      params: {
        searchTerm: searchTerm,
      },
    });
  }

  loadTags(): void {
    this.ajaxService.get(ApiRoutesV2.tagList).subscribe((resp) => this.tags$$.next(resp.body.data));
  }

  public editCustomer(businessId: string, customerToEdit: Customer): Observable<HttpResponse<null>> {
    return this.customerVoiceService.updateCustomer({
      customer: {
        businessId: businessId,
        customerId: customerToEdit.contactId,
        firstName: customerToEdit.firstName,
        lastName: customerToEdit.lastName,
        employeeFirstName: customerToEdit.employeeFirstName,
        employeeLastName: customerToEdit.employeeLastName,
        phone: customerToEdit.phone,
        email: customerToEdit.email,
        tag: customerToEdit.tag,
      },
      fieldMask: {
        paths: ['first_name', 'last_name', 'employee_first_name', 'employee_last_name', 'phone', 'email', 'tag'],
      },
    });
  }

  public addCustomers(customersToAdd: NewCustomerJson[]): Observable<HttpResponse<AjaxData>> {
    return this.appConfig.appConfig.pipe(
      switchMap((appConfig: AppConfig) => {
        customersToAdd.forEach((customer) => {
          Object.keys(customer).forEach((key) => {
            if (customer[key] === '') {
              customer[key] = undefined;
            }
          });
          customer.country = appConfig.country;
        });

        const addPayload: HttpParams = blankHttpParamsWithCustomEncoder.set(
          'customers',
          JSON.stringify(customersToAdd),
        );

        return this.ajaxService.post(ApiRoutesV2.customerAdd, addPayload.toString(), formUrlEncodedHttpOptions);
      }),
    );
  }

  public addSingleCustomer(customersToAdd: NewCustomer): Observable<HttpResponse<AjaxData>> {
    return this.appConfig.appConfig.pipe(
      switchMap((appConfig: AppConfig) => {
        let addPayload: HttpParams = new HttpParams({
          encoder: new CustomEncoder(),
        });
        addPayload = addToPayloadIfNotNull('firstName', customersToAdd.firstName, addPayload);
        addPayload = addToPayloadIfNotNull('lastName', customersToAdd.lastName, addPayload);
        addPayload = addToPayloadIfNotNull('country', appConfig.country, addPayload);
        addPayload = addToPayloadIfNotNull('phone', customersToAdd.phone, addPayload);
        addPayload = addToPayloadIfNotNull('email', customersToAdd.email, addPayload);
        addPayload = addToPayloadIfNotNull('employeeFirstName', customersToAdd.employeeFirstName, addPayload);
        addPayload = addToPayloadIfNotNull('employeeLastName', customersToAdd.employeeLastName, addPayload);
        addPayload = addToPayloadIfNotNull('tag', customersToAdd.tag, addPayload);

        return this.ajaxService.post(ApiRoutesV1.customerAddSingle, addPayload.toString(), formUrlEncodedHttpOptions);
      }),
    );
  }

  public deleteCustomers(customersToDelete: Customer[]): Observable<HttpResponse<AjaxData>> {
    const deletePayload: HttpParams = blankHttpParamsWithCustomEncoder.set(
      'customers',
      JSON.stringify(
        customersToDelete.map((customer) => {
          return {
            contactId: customer.contactId,
          };
        }),
      ),
    );
    return this.ajaxService.post(ApiRoutesV2.customerDelete, deletePayload.toString(), formUrlEncodedHttpOptions);
  }

  public customersRecentlySentEmail(customers: Customer[]): void {
    customers.forEach((customer) => {
      this.customersThatHasRecentlySentEmail[customer.contactId] = customer;
    });

    this.emailUsageService.increaseUsageBy(customers.length);
  }

  public customersRecentlySentSms(customers: Customer[]): void {
    customers.forEach((customer) => {
      this.customersThatHasRecentlySentSms[customer.contactId] = customer;
    });

    this.smsUsageService.increaseUsageBy(customers.length);
  }

  public customerExists(email: string, phone: string): Observable<HttpResponse<AjaxData>> {
    const urlParams: HttpParams = blankHttpParamsWithCustomEncoder.set('email', email).set('phone', phone);

    return this.ajaxService.get(ApiRoutesV2.customerExists, {
      params: urlParams,
    });
  }

  public switchRequestStatusToSendingIfRecentlySentRequest(customers: Customer[]): Customer[] {
    const updatedCustomers: Customer[] = cloneDeep(customers);
    updatedCustomers.forEach((customer) => {
      if (!customer.emailRequestStatus && this.customersThatHasRecentlySentEmail[customer.contactId]) {
        customer.emailRequestStatus = sendingRequestStatus;
      }
      if (!customer.smsRequestStatus && this.customersThatHasRecentlySentSms[customer.contactId]) {
        customer.smsRequestStatus = sendingRequestStatus;
      }
    });
    return updatedCustomers;
  }

  public prependNewCustomer(customer: Customer): void {
    this.customers$$.next([customer, ...this.customers$$.getValue()]);
  }

  public updateCustomer(customer: Customer): void {
    this.customers$$.next([
      ...this.customers$$.getValue().map((customerInTable) => {
        if (customerInTable.contactId === customer.contactId) {
          customerInTable.firstName = customer.firstName;
          customerInTable.lastName = customer.lastName;
          customerInTable.employeeFirstName = customer.employeeFirstName;
          customerInTable.employeeLastName = customer.employeeLastName;
          customerInTable.tag = customer.tag;
          customerInTable.email = customer.email;
          customerInTable.phone = customer.phone;
        }
        return customerInTable;
      }),
    ]);
  }

  public prependNewCustomers(customers: Customer[]): void {
    this.customers$$.next([...customers, ...this.customers$$.getValue()]);
  }

  public setFilter(fieldName: string, value: string[]): void {
    if (value.length !== 0) {
      switch (fieldName) {
        case 'emailStatuses':
          this.currentFilters.emailStatuses = value;
          break;
        case 'smsStatuses':
          this.currentFilters.smsStatuses = value;
          break;
        case 'lastRequestedDateFrom':
          this.currentFilters.lastRequestedDateFrom = value[0];
          break;
        case 'lastRequestedDateTo':
          this.currentFilters.lastRequestedDateTo = value[0];
          break;
        case 'createdDateFrom':
          this.currentFilters.createdDateFrom = value[0];
          break;
        case 'createdDateTo':
          this.currentFilters.createdDateTo = value[0];
          break;
        case 'tag':
          this.currentFilters.tag = value;
          break;
      }
    }
  }

  public clearFilter(): void {
    this.currentFilters = {
      emailStatuses: [],
      smsStatuses: [],
      lastRequestedDateFrom: '',
      lastRequestedDateTo: '',
      createdDateFrom: '',
      createdDateTo: '',
      tag: [],
    };
  }
}

export function customersFromResponse(data: any[]): Customer[] {
  return data.map((i) => {
    return {
      contactId: i.customer_id || i.contact_id,
      firstName: i.first_name || '',
      lastName: i.last_name || '',
      email: i.email || '',
      phone: i.phone || '',
      lastEmailRequested: i.email_request_last_date_sent || '',
      lastSMSRequested: i.sms_request_last_date_sent || '',
      employeeFirstName: i.employee_first_name || '',
      employeeLastName: i.employee_last_name || '',
      emailRequestStatus: i.email_review_request_status || 0,
      smsRequestStatus: i.sms_review_request_status || 0,
      smsErrorCode: i.sms_status_code,
      createdOn: i.created || '',
      reviewStarRating: i.review ? i.review.star_rating : null,
      reviewUrl: i.review ? i.review.review_url : null,
      emailLastClickedSource: i.email_last_clicked_source,
      smsLastClickedSource: i.sms_last_clicked_source,
      tag: i.tag,
    };
  });
}

export interface CustomerFilterData {
  emailStatuses: string[];
  smsStatuses: string[];
  lastRequestedDateFrom: string;
  lastRequestedDateTo: string;
  createdDateFrom: string;
  createdDateTo: string;
  tag: string[];
}
